// A small typeahead memory: remembers every value the user has typed or loaded for
// a given field (timings, keywords, …) so editors can suggest them later. Each
// store is a module-level singleton — every component using the same store sees the
// same set — and is persisted to localStorage so suggestions survive reloads.
import { computed, ref, type ComputedRef } from 'vue';

export interface SuggestionStore {
  /** Sorted, de-duplicated suggestions. */
  suggestions: ComputedRef<string[]>;
  /** Remember a value (no-op for empty/skipped/duplicate values). */
  record: (value: string | null | undefined) => void;
  /** Remember several values at once. */
  recordMany: (list: (string | null | undefined)[]) => void;
  /** Forget a single suggestion (case-insensitive). */
  forget: (value: string) => void;
  /** Forget every suggestion in this store. */
  clear: () => void;
}

/**
 * Build a suggestion store bound to a localStorage key. Returns a composable so the
 * call site reads like the other `useX()` composables; every call returns the same
 * shared state.
 */
export function createSuggestionStore(
  storageKey: string,
  options: { skip?: (value: string) => boolean } = {},
): () => SuggestionStore {
  function loadInitial(): string[] {
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === 'string') : [];
    } catch {
      return [];
    }
  }

  const items = ref<string[]>(loadInitial());

  function persist() {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items.value));
    } catch {
      /* best-effort */
    }
  }

  function record(value: string | null | undefined): void {
    const v = (value ?? '').trim();
    if (!v || options.skip?.(v)) return;
    if (items.value.some((t) => t.toLowerCase() === v.toLowerCase())) return;
    items.value = [...items.value, v];
    persist();
  }

  function recordMany(list: (string | null | undefined)[]): void {
    for (const v of list) record(v);
  }

  function forget(value: string): void {
    const v = value.toLowerCase();
    const next = items.value.filter((t) => t.toLowerCase() !== v);
    if (next.length === items.value.length) return;
    items.value = next;
    persist();
  }

  function clear(): void {
    if (!items.value.length) return;
    items.value = [];
    persist();
  }

  const suggestions = computed(() => [...items.value].sort((a, b) => a.localeCompare(b)));

  return () => ({ suggestions, record, recordMany, forget, clear });
}
